import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Container, Box, IconButton, Badge,
  Button, useTheme, useMediaQuery, Menu, MenuItem, Avatar, Divider,
  InputBase, Paper, Drawer, List, ListItem, ListItemIcon, ListItemText,
  Fab, Zoom, BottomNavigation, BottomNavigationAction, TextField, CircularProgress, Alert
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HomeIcon from '@mui/icons-material/Home';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import InventoryIcon from '@mui/icons-material/Inventory';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EditIcon from '@mui/icons-material/Edit';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { SearchAutocomplete } from './SearchHistory';
import api from '../services/api';

// 預設分類 (當 API 載入前使用)
const defaultCategories = [
  { name: '熱銷特惠', icon: <WhatshotIcon />, color: '#e65100', path: '/products?sort=discount' },
  { name: '新品上架', icon: <NewReleasesIcon />, color: '#ff9800', path: '/products?sort=newest' },
];

// 分類名稱對應的 MUI Icon 映射表 (統一使用 MUI Icons)
const categoryIconMap = {
  '廚房用品': <RestaurantIcon />,
  '收納整理': <Inventory2Icon />,
  '衛浴清潔': <HomeWorkIcon />,
  '文具辦公': <EditIcon />,
  '節慶活動': <LocalOfferIcon />,
  '居家生活': <HomeWorkIcon />,
  '旅行用品': <ShoppingBagIcon />,
  '美妝美容': <FaceRetouchingNaturalIcon />,
  '休閒娛樂': <CategoryIcon />,
  '學生用品': <EditIcon />,
  '季節商品': <WhatshotIcon />,
  '其他': <CategoryIcon />,
  '汽車用品': <CategoryIcon />,
  '寵物用品': <CategoryIcon />,
  '五金工具': <CategoryIcon />,
};

// 顏色對照表 - 橘色暖色系主題
const categoryColors = ['#e65100', '#ff6d00', '#ff9100', '#ffab00', '#f57c00', '#ef6c00', '#e65100', '#bf360c'];

const Layout = ({ children }) => {
  const { cart, cartAnimation } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { storeConfig, categories: dbCategories, shippingConfig } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileNav, setMobileNav] = useState(0);

  // SPA 內部導航時同步 document.title（server 已設對的 title，但內部導航 React 不會重 fetch HTML）
  useEffect(() => {
    const titleMap = {
      '/': '優爾商城 YourMall | 批發優惠價 滿799免運',
      '/products': '全部商品 | 優爾商城',
      '/cart': '購物車 | 優爾商城',
      '/checkout': '結帳 | 優爾商城',
      '/login': '登入註冊 | 優爾商城',
      '/member': '會員中心 | 優爾商城',
      '/shopping-guide': '購物指南 | 優爾商城',
      '/return-policy': '退換貨政策 | 優爾商城',
    };
    const path = location.pathname;
    if (titleMap[path]) {
      document.title = titleMap[path];
    } else if (path.startsWith('/checkout/complete/')) {
      document.title = '訂單完成 | 優爾商城';
    } else if (path.startsWith('/order/')) {
      document.title = '訂單查詢 | 優爾商城';
    }
    // /products/:id 由 ProductDetail.js 的 SEO 元件處理
  }, [location.pathname]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const menuOpen = Boolean(anchorEl);

  // 動態分類 - 結合預設分類和資料庫分類 (統一使用 MUI Icons)
  const categories = useMemo(() => {
    const dynamicCats = dbCategories.map((cat, idx) => ({
      name: cat.name,
      icon: categoryIconMap[cat.name] || <CategoryIcon />,
      color: categoryColors[idx % categoryColors.length],
      path: `/products?category=${encodeURIComponent(cat.name)}`
    }));
    return [...defaultCategories, ...dynamicCats];
  }, [dbCategories]);

  // 監聽滾動
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 更新底部導航狀態
  React.useEffect(() => {
    if (location.pathname === '/') setMobileNav(0);
    else if (location.pathname.startsWith('/products')) setMobileNav(1);
    else if (location.pathname === '/cart') setMobileNav(2);
    else if (location.pathname === '/member' || location.pathname === '/login') setMobileNav(3);
  }, [location]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/');
  };

  const handleSearch = async (keyword) => {
    if (keyword && keyword.trim()) {
      // 記錄搜尋
      try {
        const sessionId = localStorage.getItem('session_id') || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('session_id', sessionId);
        const userId = localStorage.getItem('user_id');

        // 先導航到搜尋結果頁
        navigate(`/products?search=${encodeURIComponent(keyword.trim())}`);

        // 異步記錄搜尋（不阻塞導航）
        api.post('/search/log', {
          session_id: sessionId,
          user_id: userId || null,
          keyword: keyword.trim(),
          results_count: 0 // 結果頁會更新實際數量
        }).catch(err => console.error('記錄搜尋失敗:', err));
      } catch (error) {
        console.error('搜尋錯誤:', error);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleSearch(searchQuery.trim());
      setSearchQuery('');
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 電子報訂閱處理
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) return;

    setNewsletterLoading(true);
    try {
      await api.post('/newsletter/subscribe', { email: newsletterEmail });
      setNewsletterSuccess(true);
      setNewsletterEmail('');
      setTimeout(() => setNewsletterSuccess(false), 5000);
    } catch (error) {
      console.error('訂閱失敗:', error);
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* 頂部促銷條 - 簡化版 */}
      <Box
        sx={{
          bgcolor: '#e65100',
          color: 'white',
          py: 0.5,
          textAlign: 'center',
          fontSize: '0.8rem'
        }}
      >
        🔥 全站 89 折起！滿 ${shippingConfig?.free_shipping_threshold || 799} 免運 | 超商取貨付款
      </Box>

      {/* Header */}
      <AppBar position="sticky" sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ py: 1, position: 'relative' }}>
          {/* 漢堡選單按鈕 */}
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              bgcolor: '#e65100',
              color: 'white',
              borderRadius: 1,
              mr: 2,
              '&:hover': { bgcolor: '#bf360c' }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            {/* 使用新設計的 LOGO 圖示 + 文字 */}
            <img
              src="/images/logo-icon.png"
              alt="優爾商城"
              style={{
                height: 40,
                width: 40,
                marginRight: 8,
                borderRadius: 8
              }}
            />
            {!isSmall && (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: '#e65100'
                }}
              >
                優爾商城
              </Typography>
            )}
          </Box>

          {/* 搜尋框 - 桌面版絕對置中，手機版靈活填充 */}
          {isMobile ? (
            <Box sx={{ flex: 1, mx: 1 }}>
              <SearchAutocomplete
                onSearch={handleSearch}
                placeholder="搜尋商品..."
              />
            </Box>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 500,
                zIndex: 1
              }}
            >
              <SearchAutocomplete
                onSearch={handleSearch}
                placeholder="搜尋商品..."
              />
            </Box>
          )}

          {/* 右側彈性空間 - 桌面版 */}
          {!isMobile && <Box sx={{ flex: 1 }} />}

          {/* 右側按鈕 - 只在桌面版顯示完整 */}
          {!isMobile && (
            <>
              {/* 會員按鈕 */}
              {isAuthenticated ? (
                <>
                  <IconButton
                    onClick={handleMenuClick}
                    sx={{ mx: 1 }}
                  >
                    <Avatar
                      src={user?.avatar}
                      sx={{ width: 36, height: 36, bgcolor: '#e53935' }}
                    >
                      {user?.name?.charAt(0) || <PersonIcon />}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { minWidth: 200, mt: 1 } }}
                  >
                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#f5f5f5' }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {user?.name || '會員'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user?.phone || user?.email}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/member?tab=0'); }}>
                      <AccountCircleIcon sx={{ mr: 1.5, color: '#666' }} fontSize="small" />
                      會員中心
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/member?tab=1'); }}>
                      <ShoppingBagIcon sx={{ mr: 1.5, color: '#666' }} fontSize="small" />
                      我的訂單
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/member?tab=4'); }}>
                      <HistoryIcon sx={{ mr: 1.5, color: '#666' }} fontSize="small" />
                      瀏覽紀錄
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                      <LogoutIcon sx={{ mr: 1.5 }} fontSize="small" />
                      登出
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  startIcon={<PersonIcon />}
                  onClick={() => navigate('/login')}
                  sx={{ mx: 1, color: '#333' }}
                >
                  登入/註冊
                </Button>
              )}
            </>
          )}

          {/* 購物車 */}
          <IconButton
            onClick={() => navigate('/cart')}
            sx={{
              ml: 1,
              animation: cartAnimation ? 'cartBounce 0.6s ease' : 'none',
              '@keyframes cartBounce': {
                '0%': { transform: 'scale(1)' },
                '25%': { transform: 'scale(1.3)' },
                '50%': { transform: 'scale(0.9)' },
                '75%': { transform: 'scale(1.15)' },
                '100%': { transform: 'scale(1)' }
              }
            }}
          >
            <Badge
              badgeContent={cart.itemCount}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: cartAnimation ? '#ff4444' : '#e65100',
                  color: 'white',
                  transition: 'all 0.3s ease',
                  animation: cartAnimation ? 'badgePop 0.6s ease' : 'none'
                },
                '@keyframes badgePop': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.5)' },
                  '100%': { transform: 'scale(1)' }
                }
              }}
            >
              <ShoppingCartIcon sx={{
                color: cartAnimation ? '#e65100' : '#333',
                fontSize: 28,
                transition: 'color 0.3s ease'
              }} />
            </Badge>
          </IconButton>
        </Toolbar>

      </AppBar>

      {/* 側邊抽屜選單 */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, bgcolor: '#fafafa' } }}
      >
        {/* 選單標題 */}
        <Box sx={{
          bgcolor: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
          background: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontIcon />
            <Typography variant="h6" fontWeight="bold">
              商品分類
            </Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 主要導航 */}
        <List sx={{ py: 1 }}>
          <ListItem
            button
            onClick={() => { setDrawerOpen(false); navigate('/'); }}
            sx={{
              py: 1.5,
              mx: 1,
              borderRadius: 2,
              '&:hover': { bgcolor: '#fff3e0' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#e65100' }}><HomeIcon /></ListItemIcon>
            <ListItemText primary="首頁" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItem>
          <ListItem
            button
            onClick={() => { setDrawerOpen(false); navigate('/products'); }}
            sx={{
              py: 1.5,
              mx: 1,
              borderRadius: 2,
              '&:hover': { bgcolor: '#fff3e0' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#e65100' }}><StorefrontIcon /></ListItemIcon>
            <ListItemText primary="全部商品" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItem>
        </List>

        <Divider sx={{ mx: 2 }} />

        {/* 分類標題 */}
        <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
          商品分類
        </Typography>

        {/* 分類列表 */}
        <List sx={{ py: 0, maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
          {categories.map((cat) => (
            <ListItem
              button
              key={cat.name}
              onClick={() => { setDrawerOpen(false); navigate(cat.path); }}
              sx={{
                py: 1.2,
                mx: 1,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#fff3e0',
                  '& .MuiListItemIcon-root': { color: '#e65100' }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: cat.color }}>
                {cat.icon}
              </ListItemIcon>
              <ListItemText
                primary={cat.name}
                primaryTypographyProps={{ fontSize: '0.95rem' }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mx: 2 }} />

        {/* 其他連結 */}
        <List sx={{ py: 1 }}>
          <ListItem
            button
            onClick={() => { setDrawerOpen(false); navigate('/shopping-guide'); }}
            sx={{
              py: 1.5,
              mx: 1,
              borderRadius: 2,
              '&:hover': { bgcolor: '#fff3e0' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#757575' }}><HelpOutlineIcon /></ListItemIcon>
            <ListItemText primary="購物說明" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItem>
          {isAuthenticated ? (
            <>
              <ListItem
                button
                onClick={() => { setDrawerOpen(false); navigate('/member'); }}
                sx={{
                  py: 1.5,
                  mx: 1,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#fff3e0' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: '#1976d2' }}><AccountCircleIcon /></ListItemIcon>
                <ListItemText primary="會員中心" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItem>
              <ListItem
                button
                onClick={() => { setDrawerOpen(false); handleLogout(); }}
                sx={{
                  py: 1.5,
                  mx: 1,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#ffebee' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: '#d32f2f' }}><LogoutIcon /></ListItemIcon>
                <ListItemText primary="登出" primaryTypographyProps={{ fontWeight: 500, color: '#d32f2f' }} />
              </ListItem>
            </>
          ) : (
            <ListItem
              button
              onClick={() => { setDrawerOpen(false); navigate('/login'); }}
              sx={{
                py: 1.5,
                mx: 1,
                borderRadius: 2,
                '&:hover': { bgcolor: '#e3f2fd' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: '#1976d2' }}><LoginIcon /></ListItemIcon>
              <ListItemText primary="登入/註冊" primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItem>
          )}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flex: 1, pb: isMobile ? 'calc(64px + env(safe-area-inset-bottom))' : 0 }}>
        {children}
      </Box>

      {/* 回到頂部按鈕 */}
      <Zoom in={showScrollTop}>
        <Fab
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 80 : 24,
            right: 24,
            bgcolor: '#e65100',
            color: 'white',
            '&:hover': { bgcolor: '#bf360c' }
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>

      {/* 浮動購物車按鈕 - 只在桌面版顯示 */}
      {!isMobile && cart.itemCount > 0 && (
        <Box
          sx={{
            position: 'fixed',
            right: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            p: 1.5,
            zIndex: 1000
          }}
        >
          <IconButton onClick={() => navigate('/cart')}>
            <Badge badgeContent={cart.itemCount} color="error">
              <ShoppingCartIcon sx={{ fontSize: 28 }} />
            </Badge>
          </IconButton>
          <Typography variant="caption" display="block" textAlign="center" color="text.secondary">
            購物車
          </Typography>
        </Box>
      )}

      {/* 手機版簡化頁尾 */}
      {isMobile && (
        <Box
          component="footer"
          sx={{
            bgcolor: '#3e2723',
            color: 'white',
            py: 1.5,
            px: 2,
            pb: 9,
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
            <Link to="/shopping-guide?tab=0" style={{ color: '#999', textDecoration: 'none', fontSize: '0.75rem' }}>關於我們</Link>
            <Link to="/shopping-guide?tab=3" style={{ color: '#999', textDecoration: 'none', fontSize: '0.75rem' }}>退換貨</Link>
            <Link to="/shopping-guide?tab=5" style={{ color: '#999', textDecoration: 'none', fontSize: '0.75rem' }}>隱私權</Link>
          </Box>
          <Box sx={{ color: '#999', fontSize: '0.7rem', my: 1, lineHeight: 1.5 }}>
            <Typography variant="span" display="block">營運公司：昀美國際貿易有限公司 (統編 42762120)</Typography>
            <Typography variant="span" display="block">客服專線：04-7380668</Typography>
            <Typography variant="span" display="block">公司地址：彰化縣彰化市彰南路三段481-1號</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block', mt: 1 }}>
            © {new Date().getFullYear()} 昀美國際貿易有限公司 All Rights Reserved
          </Typography>
        </Box>
      )}

      {/* 手機版底部導航 */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            borderTop: '1px solid #eee',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          elevation={3}
        >
          <BottomNavigation
            value={mobileNav}
            onChange={(_, newValue) => setMobileNav(newValue)}
            showLabels
          >
            <BottomNavigationAction
              label="首頁"
              icon={<HomeIcon />}
              onClick={() => navigate('/')}
            />
            <BottomNavigationAction
              label="分類"
              icon={<CategoryIcon />}
              onClick={() => navigate('/products')}
            />
            <BottomNavigationAction
              label="購物車"
              icon={
                <Badge badgeContent={cart.itemCount} color="error">
                  <ShoppingCartIcon />
                </Badge>
              }
              onClick={() => navigate('/cart')}
            />
            <BottomNavigationAction
              label="我的"
              icon={<PersonIcon />}
              onClick={() => navigate(isAuthenticated ? '/member' : '/login')}
            />
          </BottomNavigation>
        </Paper>
      )}

      {/* Footer - 精簡版 */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#3e2723',
          color: 'white',
          py: 2,
          display: isMobile ? 'none' : 'block'
        }}
      >
        <Container maxWidth="lg">
          {/* 主要連結區 */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, md: 3 },
            mb: 2
          }}>
            <Link to="/shopping-guide?tab=0" style={{ color: '#bbb', textDecoration: 'none', fontSize: '0.85rem' }}>
              關於我們
            </Link>
            <Typography sx={{ color: '#555' }}>|</Typography>
            <Link to="/shopping-guide?tab=2" style={{ color: '#bbb', textDecoration: 'none', fontSize: '0.85rem' }}>
              付款與配送
            </Link>
            <Typography sx={{ color: '#555' }}>|</Typography>
            <Link to="/shopping-guide?tab=3" style={{ color: '#bbb', textDecoration: 'none', fontSize: '0.85rem' }}>
              退換貨政策
            </Link>
            <Typography sx={{ color: '#555' }}>|</Typography>
            <Link to="/shopping-guide?tab=5" style={{ color: '#bbb', textDecoration: 'none', fontSize: '0.85rem' }}>
              隱私權政策
            </Link>
            <Typography sx={{ color: '#555' }}>|</Typography>
            <Typography variant="body2" sx={{ color: '#bbb', fontSize: '0.85rem' }}>
              客服：04-7380668
            </Typography>
            {storeConfig.line_url && (
              <>
                <Typography sx={{ color: '#555' }}>|</Typography>
                <Box
                  component="a"
                  href={storeConfig.line_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#00B900',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': { color: '#00d900' }
                  }}
                >
                  LINE 客服
                </Box>
              </>
            )}
          </Box>

          {/* 聯絡資訊 (GMC 必須) */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            color: '#aaa',
            fontSize: '0.85rem'
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>營運公司：昀美國際貿易有限公司 (統編 42762120)</Typography>
            <Typography sx={{ color: '#555' }}>|</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>客服專線：04-7380668</Typography>
            <Typography sx={{ color: '#555' }}>|</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>服務信箱：service@urstore.tw</Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
            color: '#aaa',
            fontSize: '0.85rem'
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>公司地址：彰化縣彰化市彰南路三段481-1號</Typography>
          </Box>

          {/* 版權資訊 */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            pt: 2,
            borderTop: '1px solid #4a3530'
          }}>
            <Typography variant="caption" sx={{ color: '#777' }}>
              © {new Date().getFullYear()} 昀美國際貿易有限公司 All Rights Reserved
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
